import moment from 'moment-timezone';
import React, {
  ChangeEventHandler,
  cloneElement,
  Dispatch,
  FormEvent,
  FormEventHandler,
  FunctionComponent,
  MouseEvent,
  SetStateAction,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState
} from 'react';
import DateTimePicker from 'react-datetime-picker';
import 'react-datetime-picker/dist/DateTimePicker.css';
import 'react-calendar/dist/Calendar.css';
import 'react-clock/dist/Clock.css';
//import { useRut } from 'react-rut';
import Webcam from 'react-webcam';
import { Modal } from 'reactstrap';
import {
  Button as ButtonB,
  ButtonProps,
  Checkbox,
  CheckboxProps,
  Dropdown,
  DropdownProps,
  Icon,
  Image
} from 'semantic-ui-react';
import { Fields, FieldTypes, UpdateDependencyFunc } from '../../entities/types';
import { BasicEntity, ImageFormats, SimpleObject } from '../../types';
import { toBase64 } from '../../utils/convert';
import { getEntity } from '../../utils/request';
import { capitalize, fusionFields, cleanDate } from '../../utils/utils';
import { warningPopAlert } from '../PopAlert';
import { InputType } from 'reactstrap/es/Input';
import { Value } from 'react-datetime-picker/dist/cjs/shared/types';
import GenericIcons from './GenericIcons';
import CircularProgress from '@mui/material/CircularProgress';
import '../../css/AppHome.css';

const PHOTO_WIDTH = 1920;
const PHOTO_HEIGHT = 1080;

const FieldInputText: FunctionComponent<{
  field: Fields<any>;
  onChange: ChangeEventHandler<HTMLInputElement>;
  autoFocus: boolean;
  placeholder?: string;
  value?: string | number | readonly string[];
  disabled?: boolean;
}> = ({ field, onChange, autoFocus, placeholder, value, disabled = false }) => (
  <input
    name={field.selector}
    type={field.type as InputType}
    placeholder={placeholder ? placeholder : capitalize(field.name)}
    className='generic-form-text-input'
    onChange={onChange}
    autoFocus={autoFocus}
    value={value}
    disabled={disabled}
  />
);

const FieldInputPassword: FunctionComponent<{
  field: Fields<any>;
  onChange: ChangeEventHandler<HTMLInputElement>;
  autoFocus: boolean;
  value?: string | number | readonly string[];
  disabled: boolean;
}> = ({ field, onChange, autoFocus, value, disabled = false }) => {
  const [viewPassword, setViewPassword] = useState<boolean>(false);
  const handleViewPassword = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setViewPassword(!viewPassword);
  };

  return (
    <div className='generic-form-password-input'>
      <input
        name={field.selector}
        type={viewPassword ? 'text' : 'password'}
        placeholder='*********'
        className='generic-form-text-input'
        onChange={onChange}
        autoFocus={autoFocus}
        value={value}
        disabled={disabled}
      />
      <button
        type='button'
        className='generic-form-password-input-toggle-icon vdr-gen-password-toggle'
        onClick={handleViewPassword}
        aria-label={viewPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}>
        <GenericIcons
          icon={viewPassword ? 'visibilityOffIcon' : 'visibilityIcon'}
          imageStyle={{ height: '100%' }}
        />
      </button>
    </div>
  );
};

const dateValueParser = (value: string | Date, timezone: string = moment.tz.guess()) => {
  try {
    const parsedValue =
      typeof value === 'string'
        ? new Date(moment(value).tz(timezone).format('YYYY-MM-DDTHH:mm:ss'))
        : value instanceof Date
        ? value
        : null;
    return parsedValue;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const DateTimePickerComponent: FunctionComponent<{
  name: string;
  value: string | Date;
  format: string;
  timezone: string;
  onChange: (value: Value) => void;
  minDate?: Date;
  maxDate?: Date;
  disabled: boolean;
}> = ({
  name,
  value,
  format = 'dd/MM/yyyy HH:mm:ss',
  timezone = 'UTC',
  onChange,
  minDate = undefined,
  maxDate = undefined,
  disabled = false
}) => {
  const localValue = dateValueParser(value, timezone);

  return (
    <DateTimePicker
      name={name}
      value={localValue}
      format={format}
      onChange={onChange}
      dayPlaceholder={'Día'}
      monthPlaceholder={'Mes'}
      yearPlaceholder={'Año'}
      minDate={minDate}
      maxDate={maxDate}
      className={'inputDateGlobal'}
      disableClock={true}
      disabled={disabled}
    />
  );
};

/* TODO: Fix this
const FieldInputRut: FunctionComponent<{
  field: Fields<any>;
  onChange: ChangeEventHandler<HTMLInputElement>;
  autoFocus: boolean;
  placeholder?: string;
  disabled?: boolean;
}> = ({ field, onChange, autoFocus, placeholder, disabled }) => {
  // TODO: NO DEJAR ENVIAR EL FORMULARIO SI isValid ES false
  // TODO: ARREGLAR LA OBTENCION DEL RUT COMPLETO FORMATEADO
  // TODO: QUE SE SETEE EL VALOR CUANDO SE SELECCIONA EL VALOR GUARDADO DESDE EL NAVEGADOR
  const [{ formattedValue, rawValue }, setRut] = useRut();

  const localOnChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setRut(e.target.value);
    //onChange({ target: { value: rawValue, name: field.selector } });
  };

  return (
    <input
      name={field.selector}
      type='text
      placeholder={placeholder ? placeholder : capitalize(field.name)}
      className='generic-form-text-input'
      onChange={localOnChange}
      autoFocus={autoFocus}
      value={formattedValue}
      disabled={disabled}
    />
  );
};*/

const PhotoTaker: FunctionComponent<{
  isOpen: boolean;
  closeModal: () => void;
  photoCallback: (photo: any) => void;
  format: ImageFormats;
}> = ({ isOpen, closeModal, photoCallback, format = ImageFormats.Png }) => {
  const webCamRef = React.useRef<Webcam | null>(null);

  const capture = () => {
    const imageSrc = webCamRef && webCamRef.current ? webCamRef.current.getScreenshot() : null;
    photoCallback(imageSrc);
    closeModal();
  };

  const videoConstraints = {
    width: PHOTO_WIDTH,
    height: PHOTO_HEIGHT,
    facingMode: 'environment'
  };

  return (
    <Modal isOpen={isOpen} autoFocus={true} toggle={closeModal}>
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Webcam
          ref={webCamRef}
          videoConstraints={videoConstraints}
          screenshotFormat={`image/${format}`}
          width={'100%'}
          height={'100%'}
        />
        <ButtonB style={{ width: '100%' }} type='button' onClick={capture}>
          <Icon name='camera' />
          Capturar
        </ButtonB>
      </div>
    </Modal>
  );
};

const PhotoDisplayer: FunctionComponent<{
  isOpen: boolean;
  closeModal: () => void;
  onDelete: (event: MouseEvent<HTMLButtonElement>, data: ButtonProps) => void;
  img: any;
}> = ({ isOpen, closeModal, onDelete, img }) => {
  return (
    <Modal isOpen={isOpen} autoFocus={true} toggle={closeModal}>
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Image wrapped src={img} onClick={closeModal} />
        <ButtonB style={{ width: '100%' }} type='button' onClick={onDelete}>
          <Icon name='trash' />
          Eliminar
        </ButtonB>
      </div>
    </Modal>
  );
};

const PhotoHolder: FunctionComponent<{
  onChange: (value: any[]) => void;
  constraints: {
    min: number;
    max: number;
    format: ImageFormats;
  };
}> = ({ onChange, constraints = {} }) => {
  const [images, setImages] = React.useState<any[]>([]);
  const [isCameraOpen, setIsCameraOpen] = useState<boolean>(false);
  const [isImageOpen, setIsImageOpen] = useState<boolean>(false);
  const [selectedImg, setSelectedImg] = useState<{ img: any; idx: number }>({ img: null, idx: -1 });
  const { max = 0, format = ImageFormats.Png } = constraints;

  const switchModal = (modalFn: Dispatch<SetStateAction<boolean>>, isOpen: boolean) => () =>
    modalFn(isOpen);
  const onClickImage = (img: { img: any; idx: number }) => () => {
    setSelectedImg(img);
    switchModal(setIsImageOpen, true)();
  };

  const onDeleteImage = (idx: number) => () => {
    if (idx !== -1) {
      const updatedImages = [...images];
      updatedImages.splice(idx, 1);
      setImages(updatedImages);
      setSelectedImg({ img: null, idx: -1 });
      switchModal(setIsImageOpen, false)();
    }
  };

  const photoCallBack = (photo: any) => {
    const updatedImages = [...images, photo];
    setImages(updatedImages);
    onChange(updatedImages);
  };

  const onSelectStoredPhoto: ChangeEventHandler<HTMLInputElement> = async (event) => {
    if (event.target.files && event.target.files[0]) {
      const img = await toBase64(event.target.files[0]).catch((e) => Error(e));
      if (img instanceof Error) {
        console.log('Error: ', img.message);
        return;
      }

      photoCallBack(img);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
      {images.map((img, idx) => (
        <div
          key={`form_img_${idx}`}
          onClick={onClickImage({ img, idx })}
          style={{ marginLeft: '1em', marginTop: '1em' }}>
          <Image wrapped size='tiny' src={img} />
        </div>
      ))}
      {(max === 0 || max > images.length) && (
        <div style={{ margin: '1em 0 0 1em' }}>
          <ButtonB type='button' icon='camera' onClick={switchModal(setIsCameraOpen, true)} />
          <input
            id='upload-button'
            type='file'
            accept='image/*'
            style={{ display: 'none' }}
            name='storedImage'
            onChange={onSelectStoredPhoto}
          />
          <label htmlFor='upload-button' style={{ cursor: 'pointer' }}>
            <Icon name='upload' color='black' size='big' inverted style={{ marginLeft: '0.2em' }} />
          </label>
        </div>
      )}

      <PhotoDisplayer
        isOpen={isImageOpen}
        closeModal={switchModal(setIsImageOpen, false)}
        onDelete={onDeleteImage(selectedImg.idx)}
        img={selectedImg.img}
      />
      <PhotoTaker
        isOpen={isCameraOpen}
        closeModal={switchModal(setIsCameraOpen, false)}
        photoCallback={photoCallBack}
        format={format}
      />
    </div>
  );
};

export const GenericDropDownList: FunctionComponent<{
  fieldName: string;
  onChange: (event: SyntheticEvent<HTMLElement>, data: DropdownProps) => void;
  data: SimpleObject[];
  valueField: string;
  textField: string;
  value: string | number | boolean | (string | number | boolean)[];
  required: boolean;
  loading: boolean;
  multiple: boolean;
}> = ({ fieldName, onChange, data, valueField, textField, value, required, loading, multiple }) => {
  const options = React.useMemo(
    () =>
      data && data.length > 0
        ? data.map((opt, idx) => ({
            key: `${fieldName}_${idx}`,
            value: opt[valueField],
            text: fusionFields(textField)(opt)
          }))
        : [],
    [data, valueField, textField, fieldName]
  );

  // NOTE: This make sure to set a safe value depending on the dropdown field 'multiple'
  const safeValue = multiple && !value ? [] : value;

  return (
    <Dropdown
      className='generic-form-dropdown'
      placeholder={`Seleccione ${fieldName}`}
      noResultsMessage='No hay coincidencias'
      fluid
      lazyLoad
      search
      selection={true}
      options={options}
      onChange={onChange}
      value={safeValue}
      clearable={!required}
      loading={loading}
      disabled={loading}
      multiple={multiple}
    />
  );
};

type Props<T> = {
  fields: Fields<T>[];
  isAdmin: boolean;
  onSubmit: (e: FormEvent<HTMLElement>, formFields: T) => void;
  actionName: string;
  entityToEdit?: BasicEntity & {
    [key: string]: any;
  };
  refetchFieldsOnSubmit?: boolean;
  callbackCancelButton?: () => void;
};

export const GenericForm = <T,>(props: Props<T>) => {
  const { fields, isAdmin } = props;
  const [allowedFields, setAllowedFields] = useState<Fields<T>[]>([]);
  const [referenceFields, setReferenceFields] = useState<Fields<T>[]>([]);
  const [loadingFields, setLoadingFields] = useState<SimpleObject>({});
  const [fetchedFields, setFetchedFields] = useState<SimpleObject>({});
  const [state, setState] = useState<SimpleObject>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setAllowedFields(fields.filter((f) => !(f.onlyAdmin && !isAdmin)));
    setReferenceFields(
      fields.filter(
        (f) =>
          !(
            f.onlyAdmin &&
            !isAdmin &&
            (f.type === FieldTypes.Reference || f.type === FieldTypes.MultiReference)
          )
      )
    );
  }, [isAdmin, fields]);

  useEffect(() => {
    const initialValueFormatter = (field: Fields<T>): any => {
      if (!props.entityToEdit) return undefined;

      if (field.type === FieldTypes.Password) {
        return '';
      } else if (field.type === FieldTypes.Date || field.type === FieldTypes.DateTime) {
        return dateValueParser(props.entityToEdit[field.selector], field?.timezone);
      }

      return props.entityToEdit[field.selector];
    };

    if (props.entityToEdit) {
      setState((prev) => ({
        ...prev,
        ...allowedFields.reduce(
          (obj, field) => ({
            ...obj,
            [field.selector]: initialValueFormatter(field)
          }),
          {}
        )
      }));
    }
  }, [props.entityToEdit, allowedFields]);

  const updateDependencyManager = (value: any, updateDependency: UpdateDependencyFunc) => {
    // Make a function (setLoadings) to let updateDependency manage the loading state
    const setLoadings = (fields: any[], isLoading: boolean) => {
      setLoadingFields({ ...loadingFields, ...generateLoadingFields(fields, isLoading) });
    };

    // Make a function (updateFieldValue) to let updateDependency to update the state value
    const updateFieldValue = (fieldSelector: string, fetchedValue: any) => {
      setState({ [fieldSelector]: fetchedValue });
    };

    // Make a function (updateFetchedFields) to let updateDependency to makage the fetchedFieldsData
    const updateFetchedFields = (fetchedFieldsData: SimpleObject) => {
      setFetchedFields({ ...fetchedFields, ...fetchedFieldsData });
    };

    // updateDependency is the function given to trigger an update in another field
    // updateDependency gets:
    //
    // - The current value
    // - Function to get an entity field by the selector
    // - Function setLoadingsFunction
    // - Function updateFetchedFields
    updateDependency(
      value,
      getField(fields),
      setLoadings,
      updateFetchedFields,
      updateFieldValue,
      getFieldValue,
      () => 0
    );
  };

  // Local callback to cast the semanticUI element's onChange returned data
  // into the required data for the given onChange callback
  const onValueChange = (field: Fields<T>, value: any) => {
    const { updateDependency } = field;
    setState({ ...state, [field.selector]: value });

    // If this field has an 'updateDependency'
    if (updateDependency) {
      updateDependencyManager(value, updateDependency);
    }
  };

  const dropDownListOnChange =
    (field: Fields<T>): ((event: SyntheticEvent<HTMLElement>, data: DropdownProps) => void) =>
    (_, data) => {
      onValueChange(field, data.value);
    };
  const checkboxOnChange =
    (field: Fields<T>): ((event: FormEvent<HTMLInputElement>, data: CheckboxProps) => void) =>
    (_, data) => {
      onValueChange(field, data.checked);
    };

  const photoOnChange = (field: Fields<T>) => (value: any[]) => {
    onValueChange(field, value);
  };

  const datePickerOnChange =
    (field: Fields<T>): ((value: Value) => void) =>
    (value) => {
      onValueChange(field, value);
    };
  const inputTextOnChange =
    (field: Fields<T>): ChangeEventHandler<HTMLInputElement> =>
    (input) => {
      const value = field.textUpperCase
        ? input.target.value.toUpperCase()
        : field.textLowerCase
        ? input.target.value.toLowerCase()
        : input.target.value;
      onValueChange(field, value);
    };
  const customFieldOnChange = (field: Fields<T>) => (inputValue: any) => {
    onValueChange(field, inputValue);
  };

  const generateLoadingFields = (fields: any[], isLoading: boolean) =>
    fields.reduce((acc, f) => {
      acc[`loading${capitalize(f.selector)}`] = isLoading;
      return acc;
    }, {});

  const getField = (fields: Fields<T>[]) => (fieldSelector: string) =>
    fields.find((f) => f.selector === fieldSelector);
  const getFieldValue = (fieldSelector: string) => state[fieldSelector];
  const isFieldLoading = (f: Fields<T>) => loadingFields[`loading${capitalize(f.selector)}`];

  // TODO: Should we update 'loadingFields' aftear each fetch?
  const fetchReferenceFields = useCallback(async () => {
    setLoadingFields((prev) => ({ ...prev, ...generateLoadingFields(referenceFields, true) }));

    // Fetching all the reference fields
    const fetchedFieldsArr = await Promise.all(
      referenceFields.map(async (f) => {
        if (!f.reference) return { [f.selector]: { data: [] } };
        // NOTE: You can set a manual data on the entity definition OR an API source
        const data = f.reference.data
          ? f.reference.data
          : f.reference.endpoint
          ? await getEntity(f.reference.endpoint, {
              query: {
                ...f.reference.filter,
                ...(f.reference?.endpointQuery
                  ? f.reference.endpointQuery(props.entityToEdit as T)
                  : {})
              }
            })
          : [];
        return { [f.selector]: { data } };
      })
    );

    setFetchedFields((prev) => ({
      ...prev,
      ...fetchedFieldsArr.reduce((prev, curr) => ({ ...prev, ...curr }), {})
    }));

    setLoadingFields((prev) => ({ ...prev, ...generateLoadingFields(referenceFields, false) }));
  }, [referenceFields, props.entityToEdit]);

  useEffect(() => {
    (async () => {
      await fetchReferenceFields();
    })();
  }, [fetchReferenceFields]);

  const onSubmit =
    (allowedFields: Fields<T>[]): FormEventHandler<HTMLElement> =>
    async (e) => {
      // Check required
      for (const f of allowedFields) {
        if (f.type !== FieldTypes.Boolean && f.required && !state[f.selector]) {
          e.preventDefault();
          warningPopAlert(`El campo ${f.name} es requerido`);
          return;
        }
      }

      const fieldDataParser = (f: Fields<T>) => {
        if (state[f.selector] instanceof Date) {
          const selectedDate = state[f.selector];

          return !f.timezone || f.timezone === 'UTC'
            ? cleanDate(selectedDate)
            : moment.tz(moment(selectedDate).format('YYYY-MM-DDTHH:mm:ss'), f.timezone).toDate();
        }
        return state[f.selector];
      };

      const formFields = allowedFields.reduce(
        (acc, f) => ({ ...acc, [f.selector]: fieldDataParser(f) }),
        {} as T
      );
      try {
        setIsLoading(true);
        await props.onSubmit(e, formFields);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }

      if (props.refetchFieldsOnSubmit) await fetchReferenceFields();
    };

  const getFetchedFieldData = (field: Fields<T>) =>
    fetchedFields &&
    fetchedFields[field.selector] &&
    fetchedFields[field.selector].data &&
    fetchedFields[field.selector].data.length > 0
      ? fetchedFields[field.selector].data
      : [];

  let autoFocus = false;

  const createField = (field: Fields<T>, key: number) => (
    <div key={`generic_form_${key}`} className='form-group'>
      <label id={`label_${field.selector}`} htmlFor={field.selector} className='generic-form-label'>
        {`${capitalize(field.name)}${field.required ? ' (*)' : ''}`}
      </label>

      {/*TextField*/}
      {(field.type === FieldTypes.Text || field.type === FieldTypes.Email) && (
        <>
          <FieldInputText
            field={field}
            onChange={inputTextOnChange(field)}
            value={state[field.selector] || ''}
            placeholder={field.placeholder}
            autoFocus={!autoFocus ? (autoFocus = true && true) : false}
            disabled={!!field?.disabled}
          />

          {field.validate && field.validate(state[field.selector] || '')}
        </>
      )}

      {/*Password*/}
      {field.type === FieldTypes.Password && (
        <>
          <FieldInputPassword
            field={field}
            onChange={inputTextOnChange(field)}
            value={state[field.selector] || ''}
            autoFocus={!autoFocus ? (autoFocus = true && true) : false}
            disabled={!!field?.disabled}
          />

          {field.validate && field.validate(state[field.selector] || '')}
        </>
      )}

      {/*RutField*/}
      {/* TODO: Fix 'FieldInputRut' component
      field.type === FieldTypes.Rut && (
        <FieldInputRut
          field={field}
          onChange={inputTextOnChange(field)}
          autoFocus={!autoFocus ? (autoFocus = true && true) : false}
          placeholder={field.placeholder}
          disabled={!!field?.disabled}
        />
      )
      */}

      {/*DateTimePicker*/}
      {(field.type === FieldTypes.Date || field.type === FieldTypes.DateTime) && (
        <div style={{ display: 'block' }}>
          <DateTimePickerComponent
            name={field.selector}
            onChange={datePickerOnChange(field)}
            timezone={field.timezone || 'UTC'}
            value={state[field.selector]}
            format={field.type === FieldTypes.Date ? 'dd/MM/yyyy' : 'dd/MM/yyyy HH:mm:ss'}
            disabled={!!field.disabled}
          />
        </div>
      )}

      {/*CheckBox*/}
      {field.type === FieldTypes.Boolean && (
        <div style={{ display: 'block' }}>
          <Checkbox onChange={checkboxOnChange(field)} checked={state[field.selector]} toggle />
        </div>
      )}

      {/*Reference Field (selector)*/}
      {field.reference &&
        (field.type === FieldTypes.Reference || field.type === FieldTypes.MultiReference) && (
          <GenericDropDownList
            fieldName={field.name}
            data={getFetchedFieldData(field)}
            valueField={field.reference.select}
            textField={field.reference.show}
            onChange={dropDownListOnChange(field)}
            value={state[field.selector]}
            required={!!field.required}
            loading={isFieldLoading(field)}
            multiple={field.type === FieldTypes.MultiReference}
          />
        )}

      {/*PhotoField*/}
      {field.type === FieldTypes.Photo && field.constraints && (
        <div style={{ display: 'block' }}>
          <PhotoHolder constraints={field.constraints} onChange={photoOnChange(field)} />
        </div>
      )}

      {field.type === FieldTypes.Custom && field.CustomField && (
        <>
          {field.CustomField(
            customFieldOnChange(field),
            state[field.selector] || '',
            fetchedFields || {}
          )}
        </>
      )}
    </div>
  );

  // {position:number, groupNumber:number, children: [elements]}
  const addedGroup: Array<{
    groupNumber: number;
    childrens: Array<JSX.Element>;
    position: number;
  }> = [];

  const { actionName, callbackCancelButton } = props;

  return isLoading ? (
    <div className='generic-form-loading-container'>
      <CircularProgress />
    </div>
  ) : (
    <form onSubmit={onSubmit(allowedFields)} className='generic-form-container'>
      <div className='generic-form-fields-container'>
        {allowedFields.reduce((acc, field, key) => {
          if (!field.groupNumber) {
            acc.push(createField(field, key));
            return acc;
          }
          const preExistentGroup = addedGroup.find((ag) => ag.groupNumber === field.groupNumber);

          if (!preExistentGroup) {
            const children = createField(field, key);
            acc.push(<div id={`group_${field.groupNumber}`}>{children}</div>);
            addedGroup.push({
              groupNumber: field.groupNumber,
              childrens: [children],
              position: acc.length - 1
            });
          } else {
            const groupElement = acc[preExistentGroup.position];
            const children = createField(field, key);
            preExistentGroup.childrens.push(children);

            const updatedGroup = cloneElement(
              groupElement,
              {
                style: {
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center'
                }
              },
              [...preExistentGroup.childrens]
            );

            acc[preExistentGroup.position] = updatedGroup;
          }

          return acc;
        }, [] as Array<JSX.Element>)}
      </div>
      <div className='generic-form-action-buttons-container'>
        {callbackCancelButton && (
          <button
            className='secondary-button-style'
            onClick={(e) => {
              e.preventDefault();
              callbackCancelButton();
            }}>
            Cancelar
          </button>
        )}
        <button className='primary-button-style'>{actionName || `Agregar`}</button>
      </div>
    </form>
  );
};

export default GenericForm;
